import { Navigate, Route, Routes, useParams } from 'react-router-dom-latest';
import { AppBar, Box, BoxSkeleton, Stack, Theme } from '@common-components';
import { useRetrieveCarrierSubmission } from 'hooks';
import { PartialSubmission } from 'types';
import { CARRIER_SUBMISSION_DETAILS } from 'broker/broker-routes';
import useSelectedMarket from 'broker/pages/SubmissionQueuePage/useSelectedMarket';
import WorkingOnBanner from 'broker/pages/SubmissionQueuePage/WorkingOnBanner';
import Toolbox from 'broker/pages/SubmissionWorkspacePage/components/Toolbox';
import { ToolboxNavigationSize } from 'broker/pages/SubmissionWorkspacePage/components/Toolbox/styles';
import { BrandConfigProvider } from 'broker/pages/SubmissionWorkspacePage/contexts/BrandConfigProvider';
import { SubmissionIdProvider } from 'broker/pages/SubmissionWorkspacePage/contexts/SubmissionIdContext';
import { ModalProvider } from 'broker/pages/SubmissionWorkspacePage/dialogs/modalProvider/modalProvider';
import { UiStoreProvider } from 'broker/pages/SubmissionWorkspacePage/ui-store/uiStoreProvider';
import { useWorkspaceResize } from 'broker/pages/SubmissionWorkspacePage/useWorkspaceResize';
import Header from './Header';

function ToolboxContainer({ submission }: { submission: PartialSubmission | null }) {
  const { sideBarSizeValue } = useWorkspaceResize({ isTabless: false });

  return (
    <Stack
      bgcolor="common.white"
      width={sideBarSizeValue}
      maxWidth={0.5}
      minWidth={ToolboxNavigationSize}
      overflow="auto"
      sx={{
        transition: (theme: Theme) =>
          theme.transitions.create(['width'], {
            duration: theme.transitions.duration.short,
          }),
      }}
    >
      {submission ? <Toolbox /> : <BoxSkeleton isLoading sx={{ height: 1, width: 1 }} />}
    </Stack>
  );
}

export default function CarrierSubmissionWorkspace() {
  const { id } = useParams<{ id: string }>();
  const { data: carrierSubmission } = useRetrieveCarrierSubmission({ id: id!, enabled: !!id });
  const { selectedMarket } = useSelectedMarket();

  const submission = carrierSubmission?.submission ?? null;

  return (
    <SubmissionIdProvider submissionId={submission?.id}>
      <ModalProvider>
        <UiStoreProvider>
          <BrandConfigProvider>
            {/* Redirect bare path to submission-details sub-route for Toolbox routing */}
            <Routes>
              <Route index element={<Navigate to={CARRIER_SUBMISSION_DETAILS} replace />} />
            </Routes>
            <Stack height={1}>
              <AppBar
                position="static"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  bgcolor: 'common.white',
                  color: 'text.primary',
                  px: 3,
                  py: 1,
                }}
                elevation={0}
              >
                <WorkingOnBanner selectedMarket={selectedMarket} />
              </AppBar>
              <Stack boxShadow={2} zIndex={(theme: Theme) => theme.zIndex.drawer}>
                {submission ? (
                  <Header submission={submission} />
                ) : (
                  <BoxSkeleton isLoading sx={{ height: 64, width: 1 }} />
                )}
              </Stack>
              <Stack bgcolor="background.default" flex={1} direction="row" overflow="hidden">
                <Stack position="relative" bgcolor="common.white" flex={1} overflow="auto" />
                <Box
                  sx={{
                    width: 4,
                    bgcolor: 'grey.100',
                    borderLeft: 1,
                    borderColor: 'divider',
                  }}
                />
                <ToolboxContainer submission={submission} />
              </Stack>
            </Stack>
          </BrandConfigProvider>
        </UiStoreProvider>
      </ModalProvider>
    </SubmissionIdProvider>
  );
}
